import React, { useState, useEffect } from 'react';
import { 
  RefreshCw, CheckCircle2, AlertCircle, Clock, Server, Link2, 
  ExternalLink, ArrowRight, Database, Play, Check, Copy, Settings,
  FileCode, Layers, Search, ShieldCheck, HelpCircle, Activity,
  Key, Save, Trash2, AlertTriangle
} from 'lucide-react';
import { SyncStatusInfo, SyncLogItem } from '../types';
import { auth } from '../config/firebase';

interface SyncManagerProps {
  onSyncCompleted?: () => void;
}

function formatNomenclatureList(source: any): { id: string; name: string }[] {
  if (!source) return [];
  if (Array.isArray(source)) {
    return source.map((item: any, index: number) => {
      if (typeof item === 'object' && item !== null) {
        const id = item.id !== undefined ? String(item.id) : String(index);
        const name = String(item.name || item.label || item.title || item.value || '');
        return { id, name };
      }
      return { id: String(index), name: String(item) };
    });
  }
  if (typeof source === 'object') {
    return Object.entries(source).map(([k, v]: [string, any]) => {
      if (typeof v === 'object' && v !== null) {
        const id = v.id !== undefined ? String(v.id) : k;
        const name = String(v.name || v.label || v.title || v.value || '');
        return { id, name };
      }
      return { id: k, name: String(v) };
    });
  }
  return [];
}

export default function SyncManager({ onSyncCompleted }: SyncManagerProps) {
  const [status, setStatus] = useState<SyncStatusInfo>({
    tokenConfigured: true,
    connectionStatus: 'connected',
    autoSyncEnabled: true,
    autoSyncIntervalMinutes: 360,
    lastSyncTime: null,
    isSyncing: false,
    totalProperties: 0,
    activePropertiesCount: 0,
    syncedPropertiesCount: 0,
    customPropertiesCount: 0,
    logs: []
  });
  const [loading, setLoading] = useState(false);
  const [singleEstateId, setSingleEstateId] = useState('');
  const [isSyncingSingle, setIsSyncingSingle] = useState(false);
  const [isSyncingNomenclatures, setIsSyncingNomenclatures] = useState(false);
  const [alertMessage, setAlertMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);
  const [copiedUrl, setCopiedUrl] = useState(false);
  const [nomenclatures, setNomenclatures] = useState<any | null>(null);
  const [showNomenclatures, setShowNomenclatures] = useState(false);
  const [isPurging, setIsPurging] = useState(false);
  const [showPurgeConfirm, setShowPurgeConfirm] = useState(false);
  const officialDomain = 'https://imotencentervarna.com';
  const currentHost = typeof window !== 'undefined' && !window.location.hostname.includes('run.app') && !window.location.hostname.includes('localhost')
    ? window.location.origin
    : officialDomain;
  const webhookUrl = `${currentHost}/synch.php?estateid={0}`;

  const fetchStatus = async () => {
    try {
      setLoading(true);
      const idToken = await auth.currentUser?.getIdToken();
      const res = await fetch('/api/estate-sync?action=status', {
        headers: {
          ...(idToken ? { 'Authorization': `Bearer ${idToken}` } : {})
        }
      });
      if (res.ok) {
        const ct = res.headers.get('content-type') || '';
        if (ct.includes('application/json')) {
          const data = await res.json();
          if (data?.success && data?.status) {
            setStatus(data.status);
          }
        }
      }
    } catch (err: any) {
      if (err?.name !== 'AbortError' && !err?.message?.includes('aborted')) {
        console.warn('Sync status check fallback:', err);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchNomenclatures = async () => {
    try {
      const idToken = await auth.currentUser?.getIdToken();
      const res = await fetch('/api/estate-sync?action=nomenclatures', {
        headers: {
          ...(idToken ? { 'Authorization': `Bearer ${idToken}` } : {})
        }
      });
      if (res.ok) {
        const ct = res.headers.get('content-type') || '';
        if (ct.includes('application/json')) {
          const data = await res.json();
          if (data?.success && data?.nomenclatures) {
            setNomenclatures(data.nomenclatures);
          }
        }
      }
    } catch (err: any) {
      if (err?.name !== 'AbortError' && !err?.message?.includes('aborted')) {
        console.warn('Nomenclatures check fallback:', err);
      }
    }
  };

  const handleSyncNomenclatures = async () => {
    setIsSyncingNomenclatures(true);
    setAlertMessage({ type: 'info', text: 'Извличане на актуалните номенклатури от Estate Assistant...' });
    try {
      const idToken = await auth.currentUser?.getIdToken();
      const res = await fetch('/api/estate-sync?action=nomenclatures', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(idToken ? { 'Authorization': `Bearer ${idToken}` } : {})
        }
      });
      const data = await res.json();
      if (data.success) {
        setAlertMessage({ type: 'success', text: data.message || 'Номенклатурите бяха синхронизирани успешно!' });
        if (data.nomenclatures) setNomenclatures(data.nomenclatures);
        fetchStatus();
      } else {
        setAlertMessage({ type: 'error', text: data.error || data.message || 'Грешка при обновяване на номенклатури.' });
      }
    } catch (err: any) {
      setAlertMessage({ type: 'error', text: `Грешка при връзка със сървъра: ${err.message}` });
    } finally {
      setIsSyncingNomenclatures(false);
    }
  };


  useEffect(() => {
    fetchStatus();
    fetchNomenclatures();
  }, []);

  const handleSyncSingle = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!singleEstateId.trim()) return;

    setIsSyncingSingle(true);
    setAlertMessage({ type: 'info', text: `Извличане на данни за имот #${singleEstateId}...` });
    try {
      const idToken = await auth.currentUser?.getIdToken();
      const cleanId = singleEstateId.trim().replace(/^ea-/, '');
      const res = await fetch(`/api/estate-sync?estateid=${encodeURIComponent(cleanId)}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(idToken ? { 'Authorization': `Bearer ${idToken}` } : {})
        }
      });
      const data = await res.json();
      if (data.status === 'OK' || data.success) {
        setAlertMessage({ type: 'success', text: data.message || `Имот #${cleanId} беше синхронизиран успешно!` });
        setSingleEstateId('');
        if (onSyncCompleted) onSyncCompleted();
      } else {
        setAlertMessage({ type: 'error', text: data.error || data.message || `Неуспешно синхронизиране на имот #${cleanId}.` });
      }
      fetchStatus();
    } catch (err: any) {
      setAlertMessage({ type: 'error', text: `Грешка: ${err.message}` });
    } finally {
      setIsSyncingSingle(false);
    }
  };

  const handlePurgeAll = async () => {
    setIsPurging(true);
    setAlertMessage({ type: 'info', text: 'Изчистване на всички оферти от базата / кеша...' });
    try {
      const idToken = await auth.currentUser?.getIdToken();
      const res = await fetch('/api/estate-sync?action=purge_all', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(idToken ? { 'Authorization': `Bearer ${idToken}` } : {})
        }
      });
      const data = await res.json();
      if (data.success) {
        setAlertMessage({ type: 'success', text: 'Всички оферти бяха успешно изчистени от системата!' });
        setShowPurgeConfirm(false);
        if (onSyncCompleted) onSyncCompleted();
        fetchStatus();
      } else {
        setAlertMessage({ type: 'error', text: data.error || data.message || 'Грешка при изчистване на офертите.' });
      }
    } catch (err: any) {
      setAlertMessage({ type: 'error', text: `Грешка при изчистване: ${err.message}` });
    } finally {
      setIsPurging(false);
    }
  };

  const copyWebhookUrl = async () => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(webhookUrl);
      } else {
        const tempInput = document.createElement('input');
        tempInput.value = webhookUrl;
        document.body.appendChild(tempInput);
        tempInput.select();
        document.execCommand('copy');
        document.body.removeChild(tempInput);
      }
      setCopiedUrl(true);
      setTimeout(() => setCopiedUrl(false), 2500);
    } catch {
      setCopiedUrl(true);
      setTimeout(() => setCopiedUrl(false), 2500);
    }
  };

  return (
    <div className="space-y-6 font-sans text-slate-800">
      
      {/* Alert Banner */}
      {alertMessage && (
        <div className={`p-4 rounded-xl flex items-center justify-between text-xs font-semibold shadow-sm animate-fade-in ${
          alertMessage.type === 'success' ? 'bg-emerald-50 text-emerald-900 border border-emerald-200' :
          alertMessage.type === 'error' ? 'bg-rose-50 text-rose-900 border border-rose-200' :
          'bg-amber-50 text-amber-900 border border-amber-200'
        }`}>
          <div className="flex items-center gap-2">
            {alertMessage.type === 'success' && <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />}
            {alertMessage.type === 'error' && <AlertCircle className="w-5 h-5 text-rose-600 shrink-0" />}
            {alertMessage.type === 'info' && <RefreshCw className="w-5 h-5 text-amber-600 animate-spin shrink-0" />}
            <span>{alertMessage.text}</span>
          </div>
          <button 
            onClick={() => setAlertMessage(null)} 
            className="text-slate-400 hover:text-slate-700 ml-4 font-bold"
          >
            ✕
          </button>
        </div>
      )}

      {/* Top Status Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        
        {/* Card 1: API Connection */}
        <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm flex flex-col justify-between">
          <div className="flex justify-between items-start">
            <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Estate Assistant API</span>
            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-800">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span> 
              Свързан
            </span>
          </div>
          <div className="mt-3">
            <p className="text-xs text-slate-700 font-medium truncate">
              ✓ Токен: Активен и свързан
            </p>
            <p className="text-[11px] text-slate-400 mt-0.5">
              Вътрешна системна конфигурация
            </p>
          </div>
        </div>

        {/* Card 2: Synced Listings Count */}
        <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm flex flex-col justify-between">
          <div className="flex justify-between items-start">
            <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Синхронизирани имоти</span>
            <Database className="w-4 h-4 text-[#C5A46E]" />
          </div>
          <div className="mt-2">
            <div className="text-2xl font-black text-[#0F1E32]">
              {status?.totalProperties || 0}
            </div>
            <p className="text-[11px] text-slate-500 mt-0.5">
              {status?.syncedPropertiesCount || 0} от API • {status?.customPropertiesCount || 0} ръчно въведени
            </p>
          </div>
        </div>

        {/* Card 3: 1-by-1 Mode */}
        <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm flex flex-col justify-between">
          <div className="flex justify-between items-start">
            <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Режим на качване</span>
            <CheckCircle2 className="w-4 h-4 text-emerald-500" />
          </div>
          <div className="mt-2">
            <div className="text-sm font-bold text-emerald-700">
              Строго 1 по 1
            </div>
            <p className="text-[11px] text-slate-500 mt-0.5">
              Оферти се качват само индивидуално през CRM уебхуук
            </p>
          </div>
        </div>

        {/* Card 4: Last Sync timestamp */}
        <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm flex flex-col justify-between">
          <div className="flex justify-between items-start">
            <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Последна синхронизация</span>
            <Activity className="w-4 h-4 text-amber-500" />
          </div>
          <div className="mt-2">
            <div className="text-xs font-bold text-[#0F1E32] truncate">
              {status?.lastSyncTime ? new Date(status.lastSyncTime).toLocaleString('bg-BG') : 'Очаква първоначален старт'}
            </div>
            <p className="text-[11px] text-slate-400 mt-0.5">
              Поддържа базата в пълен синхрон
            </p>
          </div>
        </div>

      </div>



      {/* Webhook Configuration & Real-Time Sync Guide (Primary Automation Pipeline) */}
      <div className="bg-gradient-to-br from-[#0F1E32] to-[#162C47] text-white p-6 md:p-8 rounded-3xl shadow-xl">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 pb-6 border-b border-slate-700/60">
          <div>
            <div className="flex items-center gap-2">
              <span className="px-2.5 py-0.5 rounded-md bg-[#C5A46E]/20 text-[#C5A46E] text-[10px] font-mono font-bold uppercase tracking-widest border border-[#C5A46E]/40">
                Автоматична Синхронизация
              </span>
            </div>
            <h2 className="text-xl font-bold mt-2">
              Estate Assistant Webhook URL
            </h2>
            <p className="text-xs text-slate-300 max-w-2xl mt-1 leading-relaxed">
              Когато брокер натисне „Качи в сайта“ в Estate Assistant, платформата автоматично вика Вашия endpoint с параметър <code className="text-[#C5A46E] font-mono">?estateid=...</code> и обявата моментално се обновява в сайта без никаква ръчна намеса!
            </p>
          </div>

          <button 
            id="btn-copy-webhook-url"
            onClick={copyWebhookUrl}
            className="bg-[#C5A46E] hover:bg-[#B1905E] text-white px-4 py-2.5 rounded-xl font-bold text-xs uppercase tracking-wider transition flex items-center gap-2 shadow shrink-0 cursor-pointer"
          >
            {copiedUrl ? <Check className="w-4 h-4 text-emerald-200" /> : <Copy className="w-4 h-4" />}
            {copiedUrl ? 'Копирано в клипборда!' : 'Копирай Webhook Линк'}
          </button>
        </div>

        {/* Webhook Endpoint Box */}
        <div className="mt-6 bg-black/30 rounded-2xl p-4 border border-white/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 font-mono text-xs">
          <div className="flex items-center gap-2 overflow-x-auto w-full">
            <span className="text-emerald-400 font-bold uppercase shrink-0">GET/POST</span>
            <span className="text-slate-300 select-all break-all">{webhookUrl}</span>
          </div>
          <span className="text-[10px] text-slate-400 uppercase tracking-widest shrink-0 font-sans font-bold">
            Активен и готов
          </span>
        </div>

        {/* How it works 4-step grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6 text-xs font-light">
          <div className="bg-white/5 p-4 rounded-xl border border-white/5">
            <div className="text-[#C5A46E] font-bold text-xs uppercase tracking-wider mb-1">1. Качване и Редакция</div>
            <p className="text-slate-300 text-[11px] leading-relaxed">
              При „Качи в сайта“ или редакция на публикуван имот в Estate Assistant, данните веднага се обновяват в сайта.
            </p>
          </div>

          <div className="bg-white/5 p-4 rounded-xl border border-white/5">
            <div className="text-[#C5A46E] font-bold text-xs uppercase tracking-wider mb-1">2. „Свали обява“ (Unpublish)</div>
            <p className="text-slate-300 text-[11px] leading-relaxed">
              При натискане на „Свали обява“, CRM изпраща <code className="text-rose-300 font-mono">&delete=1&deleted=1</code>. Имотът се скрива от сайта моментално, без дублиране!
            </p>
          </div>

          <div className="bg-white/5 p-4 rounded-xl border border-white/5">
            <div className="text-[#C5A46E] font-bold text-xs uppercase tracking-wider mb-1">3. Бутон „Запиши“ в CRM</div>
            <p className="text-slate-300 text-[11px] leading-relaxed">
              Ако дадена оферта е свалена от сайта, бутонът „Запиши“ записва само вътрешно в CRM. За да се появи на сайта, натиснете „Качи в сайта“!
            </p>
          </div>
        </div>
      </div>

      {/* Manual Single Estate ID Test (clearly secondary & marked as optional test utility) */}
      <div className="bg-white rounded-2xl border border-slate-150 p-5 shadow-sm">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="p-2.5 rounded-xl bg-slate-100 text-slate-700 shrink-0 mt-0.5">
              <Play className="w-4 h-4 text-[#C5A46E]" />
            </div>
            <div>
              <h3 className="font-bold text-[#0F1E32] text-sm">
                Тестова проверка по Estate ID (Опционално)
              </h3>
              <p className="text-xs text-slate-500 font-light mt-0.5 leading-relaxed max-w-2xl">
                В ежедневната работа <strong>не е необходимо</strong> да въвеждате оферти тук. При натискане на „Качи в сайта“ в Estate Assistant, обявата се зарежда автоматично. Това поле служи само ако искате да тествате или презаредите дадено ID директно от сайта.
              </p>
            </div>
          </div>

          <form onSubmit={handleSyncSingle} className="flex gap-2 w-full md:w-auto shrink-0">
            <input 
              id="input-single-estate-id"
              type="text"
              value={singleEstateId}
              onChange={(e) => setSingleEstateId(e.target.value)}
              placeholder="Estate ID (напр. 12345)"
              className="bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2 text-xs text-[#0F1E32] focus:outline-none focus:ring-1 focus:ring-[#C5A46E] w-full md:w-48"
            />
            <button 
              id="btn-sync-single-estate"
              type="submit"
              disabled={isSyncingSingle || !singleEstateId.trim()}
              className="bg-[#0F1E32] hover:bg-slate-800 disabled:bg-slate-200 disabled:text-slate-400 text-white font-bold px-4 py-2 rounded-xl text-xs uppercase tracking-wider transition flex items-center gap-1.5 shrink-0 cursor-pointer"
            >
              {isSyncingSingle ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <ArrowRight className="w-3.5 h-3.5" />}
              Тествай
            </button>
          </form>
        </div>
      </div>

      {/* Bulk Clear / Purge Cache Utility */}
      <div className="bg-rose-50/60 rounded-2xl border border-rose-200/80 p-5 shadow-sm">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="p-2.5 rounded-xl bg-rose-100 text-rose-700 shrink-0 mt-0.5">
              <Trash2 className="w-4 h-4 text-rose-600" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-bold text-rose-950 text-sm">
                  Изчистване на всички кеширани оферти (Bulk Clear / Purge)
                </h3>
                <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-rose-200 text-rose-800">
                  Само за администратори
                </span>
              </div>
              <p className="text-xs text-rose-800/80 font-light mt-0.5 leading-relaxed max-w-2xl">
                Изтрива натрупаните стари оферти от Cloudflare KV / локалния кеш. След изчистване брокерите могат да публикуват на чисто актуалните оферти директно от Estate Assistant.
              </p>
            </div>
          </div>

          <div className="shrink-0">
            {!showPurgeConfirm ? (
              <button
                type="button"
                id="btn-show-purge-confirm"
                onClick={() => setShowPurgeConfirm(true)}
                disabled={isPurging}
                className="bg-rose-600 hover:bg-rose-700 text-white font-bold px-4 py-2 rounded-xl text-xs uppercase tracking-wider transition flex items-center gap-1.5 shadow-sm cursor-pointer disabled:opacity-50"
              >
                <Trash2 className="w-3.5 h-3.5" />
                Изчисти всички оферти
              </button>
            ) : (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  id="btn-confirm-purge"
                  onClick={handlePurgeAll}
                  disabled={isPurging}
                  className="bg-rose-700 hover:bg-rose-800 text-white font-bold px-3 py-2 rounded-xl text-xs uppercase tracking-wider transition flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
                >
                  {isPurging ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <AlertTriangle className="w-3.5 h-3.5" />}
                  Потвърди изтриването
                </button>
                <button
                  type="button"
                  onClick={() => setShowPurgeConfirm(false)}
                  disabled={isPurging}
                  className="bg-slate-200 hover:bg-slate-300 text-slate-700 font-bold px-3 py-2 rounded-xl text-xs uppercase transition cursor-pointer"
                >
                  Отказ
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Nomenclatures Accordion */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        <button 
          onClick={() => setShowNomenclatures(!showNomenclatures)}
          className="w-full p-5 text-left flex justify-between items-center hover:bg-slate-50 transition"
        >
          <div className="flex items-center gap-2.5">
            <Layers className="w-5 h-5 text-[#C5A46E]" />
            <div>
              <h3 className="font-bold text-sm text-[#0F1E32]">Номенклатури и Справочници (Estate Assistant Nomenclatures)</h3>
              <p className="text-xs text-slate-500 font-light mt-0.5">
                Квартали, типове строителство, етапи на строеж, обзавеждане, отопление и екстри
              </p>
            </div>
          </div>
          <span className="text-xs font-bold text-[#C5A46E]">
            {showNomenclatures ? 'Скрий ▲' : 'Прегледай ▼'}
          </span>
        </button>

        {showNomenclatures && nomenclatures && (
          <div className="p-6 border-t border-slate-100 bg-slate-50 text-xs space-y-4 animate-fade-in">
            <div className="flex justify-between items-center bg-white p-3 rounded-xl border border-slate-200">
              <span className="text-slate-600 font-medium">
                Справочниците служат за правилно разчитане на кодовете на типове имоти, квартали, строителство и екстри.
              </span>
              <button
                type="button"
                onClick={handleSyncNomenclatures}
                disabled={isSyncingNomenclatures}
                className="bg-[#0F1E32] hover:bg-slate-800 disabled:bg-slate-300 text-white px-3.5 py-1.5 rounded-lg text-xs font-bold transition flex items-center gap-1.5 shrink-0"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${isSyncingNomenclatures ? 'animate-spin' : ''}`} />
                {isSyncingNomenclatures ? 'Обновяване...' : 'Синхронизирай от API'}
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white p-4 rounded-xl border border-slate-200">
                <h4 className="font-bold text-[#0F1E32] mb-2 uppercase text-[11px]">Видове Имоти</h4>
                <div className="max-h-48 overflow-y-auto space-y-1 text-slate-600 font-mono text-[11px]">
                  {formatNomenclatureList(nomenclatures.estate_types).map((item) => (
                    <div key={item.id} className="flex justify-between border-b border-slate-50 pb-0.5">
                      <span className="text-slate-400">ID {item.id}:</span>
                      <span className="font-medium text-slate-800 font-sans">{item.name}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white p-4 rounded-xl border border-slate-200">
                <h4 className="font-bold text-[#0F1E32] mb-2 uppercase text-[11px]">Типове Строителство & Етапи</h4>
                <div className="max-h-48 overflow-y-auto space-y-1 text-slate-600 font-mono text-[11px]">
                  {formatNomenclatureList(nomenclatures.build_types).map((item) => (
                    <div key={`build-${item.id}`} className="flex justify-between border-b border-slate-50 pb-0.5">
                      <span className="text-slate-400">Строителство {item.id}:</span>
                      <span className="font-medium text-slate-800 font-sans">{item.name}</span>
                    </div>
                  ))}
                  {formatNomenclatureList(nomenclatures.stage_of_construction).map((item) => (
                    <div key={`stage-${item.id}`} className="flex justify-between border-b border-slate-50 pb-0.5">
                      <span className="text-slate-400">Етап {item.id}:</span>
                      <span className="font-medium text-slate-800 font-sans">{item.name}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white p-4 rounded-xl border border-slate-200">
                <h4 className="font-bold text-[#0F1E32] mb-2 uppercase text-[11px]">Отопление, Паркиране и Обзавеждане</h4>
                <div className="max-h-48 overflow-y-auto space-y-1 text-slate-600 font-mono text-[11px]">
                  {formatNomenclatureList(nomenclatures.furnishing_types).map((item) => (
                    <div key={`furn-${item.id}`} className="flex justify-between border-b border-slate-50 pb-0.5">
                      <span className="text-slate-400">Обзавеждане {item.id}:</span>
                      <span className="font-medium text-slate-800 font-sans">{item.name}</span>
                    </div>
                  ))}
                  {formatNomenclatureList(nomenclatures.heating_types).map((item) => (
                    <div key={`heat-${item.id}`} className="flex justify-between border-b border-slate-50 pb-0.5">
                      <span className="text-slate-400">Отопление {item.id}:</span>
                      <span className="font-medium text-slate-800 font-sans">{item.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Sync Activity Logs Table */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-2">
            <Activity className="w-5 h-5 text-slate-600" />
            <h3 className="font-bold text-[#0F1E32] text-sm uppercase tracking-wider">Дневник на синхронизациите (Sync Logs)</h3>
          </div>
          <button 
            onClick={fetchStatus} 
            className="text-xs text-[#C5A46E] font-bold hover:underline flex items-center gap-1"
          >
            <RefreshCw className="w-3 h-3" /> Обнови логове
          </button>
        </div>

        {(!status?.logs || status.logs.length === 0) ? (
          <div className="text-center py-8 text-xs text-slate-400">
            Все още няма записани логове за синхронизация. Обявите се синхронизират автоматично чрез Webhook при действие „Качи в сайта“ в Estate Assistant.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead>
                <tr className="border-b border-slate-100 text-slate-400 font-bold uppercase text-[10px] tracking-wider">
                  <th className="py-2.5 px-3">Време</th>
                  <th className="py-2.5 px-3">Тип</th>
                  <th className="py-2.5 px-3">Статус</th>
                  <th className="py-2.5 px-3">Съобщение</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-slate-600">
                {status.logs.map((log: SyncLogItem) => (
                  <tr key={log.id} className="hover:bg-slate-50/80 transition">
                    <td className="py-2.5 px-3 font-mono text-[11px] text-slate-500 whitespace-nowrap">
                      {new Date(log.timestamp).toLocaleTimeString('bg-BG')} {new Date(log.timestamp).toLocaleDateString('bg-BG')}
                    </td>
                    <td className="py-2.5 px-3 uppercase text-[10px] font-bold tracking-wider">
                      <span className={`px-2 py-0.5 rounded ${
                        log.type === 'webhook' ? 'bg-purple-100 text-purple-800' :
                        log.type === 'full' ? 'bg-blue-100 text-blue-800' :
                        log.type === 'single' ? 'bg-amber-100 text-amber-800' :
                        'bg-slate-100 text-slate-700'
                      }`}>
                        {log.type}
                      </span>
                    </td>
                    <td className="py-2.5 px-3">
                      <span className={`inline-flex items-center gap-1 font-bold text-[11px] ${
                        log.status === 'success' ? 'text-emerald-600' :
                        log.status === 'error' ? 'text-rose-600' : 'text-amber-600'
                      }`}>
                        {log.status === 'success' ? '✓ Успех' : log.status === 'error' ? '✕ Грешка' : '⚠ Внимание'}
                      </span>
                    </td>
                    <td className="py-2.5 px-3 text-slate-700 max-w-md font-sans">
                      {log.message}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

    </div>
  );
}
